import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Label from '@/components/@IntranetLibrary/Label';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import {
  ReasonBody,
  ReasonCharCount,
  ReasonField,
  ReasonFooter,
  ReasonTextarea,
  reasonModalSx,
} from './styles';

/**
 * 사유 입력 모달 — 회수와 반려요청이 공유한다. 두 흐름의 차이는 문구뿐이라 한 컴포넌트로 둔다.
 * 노출 조건 판정과 API 호출은 부모 소관, 본 모달은 입력만.
 * 컨트롤드 API(open/onClose/onSubmit)를 내부 imperative Modal ref 에 동기화.
 */

type ReasonInputModalProps = {
  open: boolean;
  /** 닫기(X)/취소/백드롭 — 부모가 open=false 로 내림. 입력값은 본 모달이 초기화. */
  onClose: () => void;
  /** 확인 버튼 클릭 시 trim 된 사유로 호출. 성공 시 모달 닫기·캐시 무효화는 부모 소관. */
  onSubmit: (reason: string) => void;
  /** 부모의 API 호출 진행 중 여부. true 면 취소/확인 버튼 비활성(중복 클릭 방지). */
  submitting?: boolean;
  /** 모달 헤더 제목. submitLabel 이 없으면 확인 버튼 라벨도 같은 문구를 쓴다. */
  title: string;
  /** 확인 버튼 라벨. 없으면 title */
  submitLabel?: string;
  /** 사유 입력 전 안내 문구. 없으면 생략 */
  description?: string;
  /** 사유 입력 필드 라벨. */
  reasonLabel: string;
  /** 사유 입력 필드 placeholder. */
  reasonPlaceholder: string;
  /**
   * 사유 글자수 상한. 지정하면 하드 제한 + 카운터를 노출한다.
   * 초과 입력을 아예 막는 하드 제한인 이유는, 저장 컬럼 상한을 넘기면 서버가 500 으로 떨어져
   * 사용자가 고칠 단서를 얻지 못하기 때문이다. 미지정이면 제한 없이 동작한다(회수 사유).
   */
  reasonMaxLength?: number;
};

const ReasonInputModal = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  title,
  submitLabel,
  description,
  reasonLabel,
  reasonPlaceholder,
  reasonMaxLength,
}: ReasonInputModalProps) => {
  const { t } = useTranslation();
  const modalRef = useRef<ModalRef>(null);
  const [reason, setReason] = useState('');

  // 컨트롤드 open → imperative Modal 동기화. 재오픈 시 입력값 초기화(빈 상태 보장).
  useEffect(() => {
    if (open) {
      setReason('');
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [open]);

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const canSubmit = reason.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit || submitting) return;
    onSubmit(reason.trim());
  };

  return (
    <Modal
      ref={modalRef}
      title={title}
      size="x-small"
      handleClose={handleClose}
      sx={reasonModalSx}
      footerContent={
        <ReasonFooter>
          <BoxButton
            variant="secondary"
            size="large"
            disabled={submitting}
            onClick={handleClose}
          >
            {t('취소')}
          </BoxButton>
          <BoxButton
            variant="primary"
            size="large"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
          >
            {submitLabel ?? title}
          </BoxButton>
        </ReasonFooter>
      }
    >
      <ReasonBody>
        {description && <Body2>{description}</Body2>}
        <ReasonField>
          <Label size={14} isRequired title={reasonLabel} />
          <ReasonTextarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonPlaceholder}
            maxLength={reasonMaxLength}
          />
          {reasonMaxLength !== undefined && (
            <ReasonCharCount>
              {reason.length}/{reasonMaxLength}
            </ReasonCharCount>
          )}
        </ReasonField>
      </ReasonBody>
    </Modal>
  );
};

export default ReasonInputModal;
